import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { CustomerModel } from 'src/app/models/customer-model';
import type { ILoginRequestData, ILoginResponsePayload } from 'src/app/interfaces/authentication.interface';
import { LoginResponseComments } from 'src/app/interfaces/authentication.interface';
import { Navigator } from "src/app/components/menu/navigator";

import { AppsflyerService } from '../marketing/appsflyer.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { ConfigService } from '../config/config.service';
import { EventsService } from '../event/event.service';
import { LobbyService } from '../lobby/lobby.service';
import { eMfaStatuts, UserSessionService } from '../user-session/user-session.service';
import { UniqueIDService } from '../unique-id/unique-id.service';
import { BoydRewardsService } from '../boyd-rewards/boyd-rewards.service';
import { ResponsibleGamingService } from '../responsible-gaming-service/responsible-gaming.service';

@Injectable({
  providedIn: 'root'
})

export class LoginService {

  _loginInfoSubject: BehaviorSubject<any>;
  public loginInfo: ILoginResponsePayload;
  public customerIPAddress: any;
  public customerLastLogoutReason: any;
  public reconfirmPersonalDetails: any;
  public customerID: string | number;
  boydSubscription: Subscription

  constructor(
    private navigator: Navigator,
    private lobbyService: LobbyService,
    private userSessionService: UserSessionService,
    private eventsService: EventsService,
    private config: ConfigService,
    public appsFlyerService: AppsflyerService,
    public authenticationService: AuthenticationService,
    public uniqueIDService: UniqueIDService,
    public responsibleGamingProvider: ResponsibleGamingService,
    private boydRewardsService: BoydRewardsService,
  ) {
    this._loginInfoSubject = new BehaviorSubject(undefined);
  }

  public loginCustomer(data: ILoginRequestData) {

    let response = new Subject();
    this.authenticationService.callLogin(data).subscribe(resp => {
      if (!!resp.result && resp.payload) {
        this.setUserLogin(resp.payload);
        response.next(resp.payload);
        response.complete();
      }
    }, (error) => {
      response.error(error);
      response.complete();
    });
    return response;
  }

  public setUserLogin(loginInfo: ILoginResponsePayload) {
    if (!!loginInfo) {
      localStorage.setItem('userSession.loginInfo', JSON.stringify(loginInfo));
      this.loginInfo = loginInfo;
      // this.loginInfo.hasNotUpdatedHouseRules = true;
    } else {
      localStorage.removeItem('userSession.loginInfo');
      this.loginInfo = loginInfo;
    }
    this.userSessionService.setUserStatus(this.loginInfo)
    this._loginInfoSubject.next(this.loginInfo);
  }

  public getUserLogin() {
    return this._loginInfoSubject;
  }

  async setLoginResponseInStorage() {
    const loginInfo = this.loginInfo;
    if (loginInfo) {
      localStorage.setItem('clientApp', this.config.getClientApp());
      if ('lastIPAddress' in loginInfo) {
        this.setCustomerIPAddress(loginInfo.lastIPAddress);
      }
      if ('lastLogoutReason' in loginInfo) {
        this.setCustomerLastLogoutReason(loginInfo.lastLogoutReason);
      }

      if ('customerId' in loginInfo) {
        this.setCustomerID(loginInfo.customerId)
      }

      if ('reconfirmPersonalDetails' in loginInfo) {
        this.setCustomerReconfirmationPersonalInfo(loginInfo.reconfirmPersonalDetails);
      }

      const needMFA = this.config.getConfig().FEATURE_MFA_FORCE_LOGIN === '1' ||
        loginInfo?.comments?.indexOf(LoginResponseComments.CUSTOMER_MFA_REQUIRED) > -1
      if (needMFA) {
        if (loginInfo.deviceId) {
          this.uniqueIDService.setMFADeviceIdAsObservable(loginInfo.deviceId);
        }
        this.startMFA();
      }
      else {
        this.userSessionService.refreshCustomer().subscribe(res => { })
        this.checkLoginActions();
      }
    }
  }

  public setCustomerIPAddress(IPAddress: any) {
    localStorage.setItem('lastIPAddress', IPAddress);
    this.customerIPAddress = IPAddress;
  }

  public getCustomerIPAddress() {
    return this.customerIPAddress;
  }

  public setCustomerLastLogoutReason(LogoutReason: any) {
    localStorage.setItem('lastLogoutReason', LogoutReason);
    this.customerLastLogoutReason = LogoutReason;
  }

  public getCustomerLastLogoutReason() {
    return this.customerLastLogoutReason;
  }

  public setCustomerReconfirmationPersonalInfo(reconfirmationInfo: any) {
    localStorage.setItem('reconfirmPersonalDetails', reconfirmationInfo);
    this.reconfirmPersonalDetails = reconfirmationInfo;
  }

  public getCustomerReconfirmationPersonalInfo() {
    return this.reconfirmPersonalDetails;
  }

  public clearCustomerReconfirmationPersonalInfo() {
    localStorage.removeItem('reconfirmPersonalDetails');
  }

  public setCustomerID(customerID: any) {
    localStorage.setItem('customerID', customerID);
    this.customerID = customerID;
  }

  public getCustomerID() {
    return this.customerID;
  }

  public getCustomerIDFromLocalStorage() {
    return localStorage.getItem('customerID');
  }

  private startMFA() {
    const isMFA = this.config.getConfig().FEATURE_MFA_ENABLED == "1" ? true : false;
    const isSMS_OR_EMAIL = this.config.getConfig().FEATURE_MFA_TYPE ?? 'SMS';
    const mfa_statuses = this.config.getConfig().FEATURE_MFA_STATUSES ?? 'ACTIVE, REAL_LOCKED, SELF_EXCLUDED_COOL_OFF, FUNDS_HELD';

    // TODO: user already logged in at this step,
    // if 'refreshCustomer' API throw error or any subscriber throws error, then MFA logic is broken
    this.userSessionService.refreshCustomer().subscribe((customer: CustomerModel) => {
      if (isMFA && mfa_statuses.toUpperCase().includes(customer.status.toUpperCase())) {
        this.userSessionService.setMFAStatus(eMfaStatuts.required);
        if (isSMS_OR_EMAIL == 'SMS') {
          this.navigator.navigateTo("SmsLoginComponent", { forceModal: true }, { customerId: customer.customerId, callback: this.checkLoginActions })
        } else {
          this.navigator.navigateTo("CodeLoginPage", { forceModal: true }, { customerId: customer.customerId, callback: this.checkLoginActions })
        }

        return;
      }
      this.checkLoginActions();
    });
  }

  public checkLoginActions = () => {
    if ('hasNotUpdatedToc' in this.loginInfo && this.loginInfo.hasNotUpdatedToc === true) {
      delete this.loginInfo.hasNotUpdatedToc;
      this.navigator.navigateTo("UpdateTocComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    } else if ('hasNotUpdatedPrivacyPolicy' in this.loginInfo && this.loginInfo.hasNotUpdatedPrivacyPolicy === true) {
      delete this.loginInfo.hasNotUpdatedPrivacyPolicy;
      this.navigator.navigateTo("UpdatePrivacyPolicyComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callback: this.checkLoginActions })
    } else if ('hasNotUpdatedHouseRules' in this.loginInfo && this.loginInfo.hasNotUpdatedHouseRules === true) {
      delete this.loginInfo.hasNotUpdatedHouseRules;
      this.navigator.navigateTo("UpdateHouseRulesComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callback: this.checkLoginActions })
    } else if ('hasExceededDepositLimit' in this.loginInfo) {
      delete this.loginInfo.hasExceededDepositLimit;
      this.navigator.navigateTo("DepositThresholdComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    } else if ('strongAuthenticationRequired' in this.loginInfo) {
      localStorage.setItem('strongAuthenticationRequired', 'true');
      delete this.loginInfo.strongAuthenticationRequired;
      this.navigator.navigateTo("PinLoginComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    }

    else {
      this.afterLoginActions();
    }
  }

  afterLoginActions = () => {

    this.userSessionService.refreshCustomer().subscribe((customer: CustomerModel) => {
      this.lobbyService.setCurrentLoginTime(customer.currentLoginTime);
      this.eventsService.publish('event:login', {
        customer_id: customer.customerId,
      });
      //when using tab name - it should match with app.module.ts route name


      this.eventsService.publish('action:changeTab', 'casino');


      this.eventsService.publish('event:update:banners');
      let params = {}
      this.eventsService.publish('event:appInit:init', params);
    });
  }
}

